import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface CaughtError {
  error: unknown;
  componentStack: string | null;
  reset: () => void;
}

interface Props {
  children: ReactNode;
  fallback: (caught: CaughtError) => ReactNode;
  /** Runs before the boundary clears its error, so the child re-mounts against fresh state. */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: unknown;
  componentStack: string | null;
}

/**
 * Only `componentDidCatch` sees React's component stack, so capture lives here and screens stay
 * presentational.
 */
export class ErrorCapture extends Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error:', error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  reset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return this.props.fallback({
      error: this.state.error,
      componentStack: this.state.componentStack,
      reset: this.reset,
    });
  }
}
